import React, { useEffect, useMemo, useState, useRef, Children, Component, isValidElement } from 'react';
import { ChevronDown, Check } from 'lucide-react';
export function Label({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <label className={`block text-[13px] font-medium text-slate-600 mb-1.5 ${className}`} data-id="element-1578">
      {children}
    </label>;
}
export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`block w-full rounded-xl border-slate-200 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border transition-colors hover:border-slate-300 ${props.className || ''}`} data-id="element-1579" />;
}
// Custom Select Component to replace native select
interface SelectOption {
  value: string;
  label: React.ReactNode;
}
interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  value: string;
  onChange: (e: {
    target: {
      value: string;
    };
  }) => void;
  options?: SelectOption[]; // Optional: if provided, uses custom UI. If not, falls back to parsing children (for backward compatibility)
  searchable?: boolean;
}
export function Select({
  value,
  onChange,
  options: propOptions,
  children,
  className = '',
  searchable = false,
  ...props
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Parse children to extract options if propOptions is not provided
  const options = useMemo(() => {
    if (propOptions) return propOptions;
    const extractedOptions: SelectOption[] = [];
    Children.forEach(children, child => {
      if (isValidElement(child) && child.type === 'option') {
        extractedOptions.push({
          value: child.props.value,
          label: child.props.children
        });
      }
    });
    return extractedOptions;
  }, [propOptions, children]);
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      if (searchable && searchInputRef.current) {
        // Small delay to ensure the dropdown is rendered before focusing
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    }
  }, [isOpen, searchable]);
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(opt => opt.value.toLowerCase().includes(query) || typeof opt.label === 'string' && opt.label.toLowerCase().includes(query));
  }, [options, searchable, searchQuery]);
  return <div className={`relative ${className}`} ref={containerRef} data-id="element-1580">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`relative w-full bg-white border border-slate-200 rounded-xl shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors hover:border-slate-300 ${className}`} {...props as any} data-id="element-1581">
        <span className="block truncate" data-id="element-1582">{selectedOption?.label || value}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-1583">
          <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-1584" />
        </span>
      </button>

      {isOpen && <div className="absolute z-50 mt-1.5 min-w-full w-max bg-white shadow-lg max-h-60 rounded-xl py-1 text-base ring-1 ring-slate-200 overflow-hidden flex flex-col focus:outline-none sm:text-sm right-0" data-id="element-1585">
          {searchable && <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10" data-id="element-1586">
              <input ref={searchInputRef} type="text" className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onClick={e => e.stopPropagation()} data-id="element-1587" />
            </div>}
          <div className="overflow-auto flex-1" data-id="element-1588">
            {filteredOptions.length === 0 ? <div className="py-3 px-4 text-sm text-slate-500 text-center" data-id="element-1589">
                No results found
              </div> : filteredOptions.map(option => <div key={option.value} className={`cursor-default select-none relative py-2 pl-3 pr-9 transition-colors ${value === option.value ? 'text-blue-900 bg-blue-50' : 'text-slate-900 hover:bg-slate-50'}`} onClick={() => {
          onChange({
            target: {
              value: option.value
            }
          });
          setIsOpen(false);
        }} data-id="element-1590">
                  <span className={`block truncate ${value === option.value ? 'font-semibold' : 'font-normal'}`} data-id="element-1591">
                    {option.label}
                  </span>
                  {value === option.value && <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600" data-id="element-1592">
                      <Check className="h-4 w-4" aria-hidden="true" data-id="element-1593" />
                    </span>}
                </div>)}
          </div>
        </div>}
      {/* Hidden native select for form submission compatibility if needed */}
      <select value={value} onChange={e => onChange(e)} className="hidden" name={props.name} data-id="element-1594">
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-1595">
            {opt.label}
          </option>)}
      </select>
    </div>;
}